/**
 * `@aquilixshop/i18n/lazy-bundled` — ONE bundled dictionary, fetched when a browser host learns which
 * language it is actually rendering.
 *
 * ── The bill this module exists to stop paying ─────────────────────────────────────────────────────────
 *
 * {@link ./bundled} registers all twenty-six at once, and that is right for the storefront's Node process,
 * where the dictionaries cost nothing. It is wrong for a BROWSER host. The builder imported it (through
 * `@aquilixshop/ui-react/bundled`) from a module its entry chunk pulls in statically, so every cold builder
 * load carried twenty-six dictionaries — 918 KB raw, measured by sourcemap attribution on the preprod entry
 * chunk 2026-09-16, ~31% of the 802 KB that chunk weighs over the wire. The builder edits ONE store, in ONE
 * language, and the other twenty-five were never read. That is the 25-second spinner the founder reported
 * three times: the sentence that made the wait visible (the inline pre-boot state, 2026-09-14) never
 * shortened it.
 *
 * ── The shape that fixes it ────────────────────────────────────────────────────────────────────────────
 *
 * A literal `import()` per locale, written out one line each rather than built from a template string:
 * bundlers only split what they can see statically, and `import('./locales/' + code)` makes Vite emit a
 * glob of all twenty-six again — the exact bill we are here to stop paying.
 *
 * French is NOT in the map. It is the floor of every lookup ({@link ./bundled-registry}), already in the
 * bundle wherever `t()` runs, and asking for it here resolves without a network round-trip.
 *
 * Registration is ADDITIVE ({@link ./bundled-registry!addBundledDictionary}): a host that calls this twice,
 * for two locales, ends up able to translate both. Nothing it registers can displace what a server-side
 * `@aquilixshop/i18n/bundled` import already put there.
 */
import { addBundledDictionary } from './bundled-registry';
import { FALLBACK_LOCALE } from './fallback';
/** locale → its own chunk. Literal specifiers on purpose; see the module note. */
const LOADERS = {
    ar: () => import('./locales/ar'),
    be: () => import('./locales/be'),
    bg: () => import('./locales/bg'),
    bs: () => import('./locales/bs'),
    cs: () => import('./locales/cs'),
    de: () => import('./locales/de'),
    el: () => import('./locales/el'),
    en: () => import('./locales/en'),
    es: () => import('./locales/es'),
    et: () => import('./locales/et'),
    hr: () => import('./locales/hr'),
    hu: () => import('./locales/hu'),
    it: () => import('./locales/it'),
    lt: () => import('./locales/lt'),
    lv: () => import('./locales/lv'),
    mk: () => import('./locales/mk'),
    pl: () => import('./locales/pl'),
    pt: () => import('./locales/pt'),
    ro: () => import('./locales/ro'),
    ru: () => import('./locales/ru'),
    sk: () => import('./locales/sk'),
    sl: () => import('./locales/sl'),
    sq: () => import('./locales/sq'),
    sr: () => import('./locales/sr'),
    uk: () => import('./locales/uk'),
};
/** In-flight / settled loads, so N callers for one locale share ONE chunk fetch. */
const inFlight = new Map();
/** Every locale this module can fetch — the catalogue minus French, which never needs fetching. */
export function lazyLoadableLocales() {
    return Object.keys(LOADERS);
}
/**
 * Make [locale] translatable in THIS bundle, then resolve.
 *
 * Await it before showing text in that language: until it resolves `t()` answers from French, which is
 * the contract's floor and reads as a flash of the wrong language rather than a crash. Unknown locales and
 * a failed chunk fetch resolve quietly for the same reason — a dictionary that cannot be fetched must cost
 * the seller French sentences, never a blank editor.
 */
export function ensureBundledDictionary(locale) {
    if (locale === FALLBACK_LOCALE)
        return Promise.resolve();
    const existing = inFlight.get(locale);
    if (existing)
        return existing;
    const load = LOADERS[locale];
    if (!load)
        return Promise.resolve();
    const p = load()
        .then((mod) => {
        const dict = mod[locale];
        if (dict)
            addBundledDictionary(locale, dict);
    })
        .catch(() => {
        // Let the next caller retry: a chunk lost to a flaky line is not a permanent verdict.
        inFlight.delete(locale);
    });
    inFlight.set(locale, p);
    return p;
}
//# sourceMappingURL=lazy-bundled.js.map